import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { BUSINESS_COLLECTION } from "./constants";
import { Lead } from "@/components/business/leads/types/LeadTypes";
import { parseISO, parse, format } from "date-fns";

/**
 * Format audio duration from seconds to MM:SS format
 */
const formatAudioDuration = (seconds: number | string | undefined): string => {
  if (!seconds) return "00:00";

  // Convert to number if it's a string
  const totalSeconds =
    typeof seconds === "string" ? parseInt(seconds, 10) : seconds;

  // Handle invalid input
  if (isNaN(totalSeconds)) return "00:00";

  // Calculate minutes and seconds
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = Math.floor(totalSeconds % 60);

  // Format with leading zeros
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = remainingSeconds.toString().padStart(2, "0");

  return `${formattedMinutes}:${formattedSeconds}`;
};

/**
 * Format date to MM/DD/YYYY HH:mm A format
 */
const formatDate = (dateString: string): string => {
  try {
    let date = parseISO(dateString);

    // If parseISO fails or the string doesn't contain a 'T',
    // try parsing with a known format like "yyyy-MM-dd hh:mm a XXX".
    if (isNaN(date.getTime())) {
      if (!dateString.includes('T')) {
        date = parse(dateString, 'yyyy-MM-dd hh:mm a XXX', new Date());
      } else {
        date = new Date(dateString);
      }
    }

    // Final fallback using built-in Date parser
    if (isNaN(date.getTime())) {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return '';
    }

    return format(date, 'MM/dd/yyyy hh:mm a');
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};


/**
 * Fetch CTM (Call Tracking Metrics) leads from Firestore
 */
export const fetchLeadsForBusiness = async (
  businessId: string
): Promise<Lead[]> => {
  try {
    console.log(`Fetching leads for business ${businessId}`);

    // Reference to the leads collection
    const leadsRef = collection(db, BUSINESS_COLLECTION, businessId, "leads");

    // Create a query ordered by unix_time (newest first)
    const q = query(leadsRef, orderBy("unix_time", "desc"));

    // Execute the query
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log(`No leads found for account ${businessId}`);
      return [];
    }

    // Convert the documents to Lead objects
    const leads: Lead[] = snapshot.docs
      .map((doc) => {
        const data = doc.data();
        // Skip leads that are neither inbound/outbound calls nor forms
        if (!["inbound", "outbound", "form"].includes(data.direction)) {
          return null;
        }

        // Map CTM status to our LeadStatus type
        let status: Lead["status"] = "Call Answered";

        // Detect calls
        if (data.call_status === "completed" && data.direction === "inbound") {
          status = "Call Answered";
        } else if (
          data.call_status === "no answer" &&
          data.direction === "inbound"
        ) {
          status = "Call Missed";
        } else if (
          data.call_status === "completed" &&
          data.direction === "outbound"
        ) {
          status = "Call Returned";
        } else if (
          data.call_status === "returned" &&
          data.direction === "call"
        ) {
          status = "Call Returned";
        }

        // Detect forms
        else if (
          data.direction === "form" &&
          Array.isArray(data.callbacks) &&
          data.callbacks.length > 0
        ) {
          status = "Form Replied";
        } else if (data.direction === "form") {
          status = "Form Received";
        }

        // Format the date from called_at
        const date = data.called_at ? formatDate(data.called_at) : "";

        // Handle form data if present
        const formFields = {
          message:
            data.form?.custom?.find((f: { id: string; value: string }) => f.id === "message")?.value ||
            "",
          zipCode:
            data.form?.custom?.find((f: { id: string; value: string }) => f.id === "zip_code")?.value ||
            data.postal_code ||
            "",
          email: data.form?.email || data.email || "",
        };
        const outcome = data.lead_outcome || "Lost (unknown)";
        
        const lead: Lead = {
          id: data.id?.toString() || doc.id,
          name: data.name || data.cnam || "Unknown Caller",
          phone: data.caller_number_format || data.caller_number || "",
          status: data.lead_status || status || null,
          outcome: outcome,
          value: data.value || "",
          date,
          type: data.direction === "form" ? "Form" : "Call",
          aiSummary: data.summary || "",
          audioDuration: formatAudioDuration(data.duration),
          audioUrl: data.audio || undefined,
          transcription_json: data.transcription_json || undefined,
          formFields: data.direction === "form" ? formFields : undefined,
          ga: data.ga ? {
            ga4: data.ga.ga4 || {}
          } : undefined
        };

        return lead;
      })
      .filter((lead): lead is Lead => lead !== null);
    
    // Filter out duplicate form submissions based on phone number and message
    const uniqueLeads = filterDuplicateFormSubmissions(leads);
    return uniqueLeads;
  } catch (error: unknown) {
    console.error(`Error fetching leads:`, error);
    return [];
  }
};

/**
 * Filter out duplicate form submissions based on phone number and message
 * Only checks adjacent items using a sliding window of 2
 * On finding a duplicate, keeps the most recent (later in the array) and discards the earlier one
 */
const filterDuplicateFormSubmissions = (leads: Lead[]): Lead[] => {
  // If we have 1 or fewer leads, no duplicates to filter
  if (leads.length <= 1) {
    return leads;
  }
  
  const result: Lead[] = [];
  
  // Start with the first lead
  let previousLead: Lead | null = leads[0];
  
  // Iterate through the remaining leads (starting from index 1)
  for (let i = 1; i < leads.length; i++) {
    const currentLead = leads[i];
    
    // Skip comparison if either lead is not a form submission
    if (previousLead?.type !== "Form" || currentLead.type !== "Form") {
      // If previous lead exists and we're not discarding it, add it to result
      if (previousLead) {
        result.push(previousLead);
      }
      previousLead = currentLead;
      continue;
    }
    
    // Get phone and message for both leads
    const prevPhone = previousLead?.phone || "";
    const prevMessage = previousLead?.formFields?.message || "";
    const currPhone = currentLead.phone || "";
    const currMessage = currentLead.formFields?.message || "";
    
    // Skip comparison if any phone or message is empty
    if (!prevPhone || !prevMessage || !currPhone || !currMessage) {
      // If previous lead exists and we're not discarding it, add it to result
      if (previousLead) {
        result.push(previousLead);
      }
      previousLead = currentLead;
      continue;
    }
    
    // Check if they're duplicates (same phone and message)
    if (prevPhone === currPhone && prevMessage === currMessage) {
      console.log(`Found adjacent duplicate: discarding ${previousLead?.id}, keeping ${currentLead.id}`);
      // Discard the previous lead (don't add it to result)
      // Keep the current lead as the new previous lead
      previousLead = currentLead;
    } else {
      // Not duplicates, add previous lead to result
      result.push(previousLead);
      previousLead = currentLead;
    }
  }
  
  // Add the last lead if it exists
  if (previousLead) {
    result.push(previousLead);
  }
  
  return result;
};
